<template>
    <div class="dashboard-wrapper">
        <DashboardDevicesBar
            class="dashboard-topbar"
            :autoMode="autoMode"
            :deviceIds="deviceProject"
            :activeDeviceId="activeDeviceId"
            :activeProgressPct="activeProgressPct"
            @select-device="goToDeviceSlide"
        />
        <v-carousel
            v-model="model"
            @change="onCarouselChange"
            :cycle="autoMode"
            :interval="slideDurationMs"
            height="100%"
            class="dashboard-carousel"
            light
            hide-delimiters
            show-arrows-on-hover
        >
            <v-carousel-item>
                <v-sheet height="100%" tile light color="white">
                    <v-row class="fill-height" align="center" justify="center">
                        <v-col cols="12" style="height: 100%">
                            <DataMap :active="model === 0" />
                        </v-col>
                    </v-row>
                </v-sheet>
            </v-carousel-item>

            <v-carousel-item v-if="isDev">
                <v-sheet height="100%" tile light color="white">
                    <v-row class="fill-height" align="center" justify="center">
                        <v-col cols="12" style="height: 100%">
                            <DataMap :heatMap="true" :active="model === 1" />
                        </v-col>
                    </v-row>
                </v-sheet>
            </v-carousel-item>

            <v-carousel-item v-for="(device, index) in deviceProject" :key="`${project}-${device}`">
                <v-sheet height="100%" tile light color="white">
                    <v-row class="fill-height" align="start" justify="center" no-gutters>
                        <v-col cols="12" class="fill-height d-flex">
                            <SimpleChars
                                class="fill-height flex-grow-1"
                                :key="`chart-${project}-${device}`"
                                :deviceId="device"
                                :isActive="model === (index + deviceSlidesOffset)"
                            />
                        </v-col>
                    </v-row>
                </v-sheet>
            </v-carousel-item>

            <v-carousel-item :key="`${project}-all`">
                <v-sheet height="100%" tile light color="white">
                    <v-row class="fill-height" align="start" justify="center" no-gutters>
                        <v-col cols="12" class="fill-height d-flex">
                            <SimpleChars
                                class="fill-height flex-grow-1"
                                :key="`chart-${project}-all-devices`"
                                :devices="deviceProject"
                                :isActive="model === comparativeSlideIndex"
                            />
                        </v-col>
                    </v-row>
                </v-sheet>
            </v-carousel-item>
        </v-carousel>
    </div>
</template>

<script>
import moment from 'moment'
import SimpleChars from '@/components/components/SimpleChars'
import DataMap from '@/components/main/DataMap'
import DashboardDevicesBar from '@/components/components/DashboardDevicesBar'

const EXCLUDED_DASHBOARD_DEVICE_ID = 'device043'

export default {
    name: 'Compare',

    data: () => ({
        model: 0,
        autoMode: true,
        activeProgressPct: 0,
        slideDurationMs: 20000,
        progressTickMs: 200,
        slideStartedAt: null,
        progressIntervalId: null,
        lastHandledCommandSeq: 0
    }),

    methods: {
        onCarouselChange() {
            this.$nextTick(() => {
                this.$forceUpdate()
            })
        },

        toggleAutoMode() {
            this.autoMode = !this.autoMode
        },

        goToDeviceSlide(deviceId) {
            if (!Object.prototype.hasOwnProperty.call(this.deviceSlideIndexMap, deviceId)) return
            this.goToSlide(this.deviceSlideIndexMap[deviceId])
        },

        goToSlide(slideIndex) {
            if (typeof slideIndex !== 'number') return
            const lastValidIndex = Math.max(this.totalSlides - 1, 0)
            this.model = Math.min(Math.max(slideIndex, 0), lastValidIndex)
        },

        goToPreviousSlide() {
            if (this.totalSlides <= 1) return
            this.model = (this.model - 1 + this.totalSlides) % this.totalSlides
        },

        goToNextSlide() {
            if (this.totalSlides <= 1) return
            this.model = (this.model + 1) % this.totalSlides
        },

        getSlideType(slideIndex) {
            if (slideIndex === 0) return 'map'
            if (this.isDev && slideIndex === 1) return 'heat'
            if (slideIndex === this.comparativeSlideIndex) return 'comparative'

            const firstDeviceIndex = this.deviceSlidesOffset
            const lastDeviceIndex = firstDeviceIndex + this.deviceProject.length - 1
            if (slideIndex >= firstDeviceIndex && slideIndex <= lastDeviceIndex) {
                return 'device'
            }

            return 'unknown'
        },

        restartProgress() {
            this.slideStartedAt = Date.now()
            this.activeProgressPct = 0
        },

        clearProgressTracking() {
            if (!this.progressIntervalId) return
            clearInterval(this.progressIntervalId)
            this.progressIntervalId = null
        },

        startProgressTracking({ reset = true } = {}) {
            this.clearProgressTracking()

            if (!this.autoMode) {
                this.activeProgressPct = 0
                return
            }

            if (reset || !this.slideStartedAt) {
                this.restartProgress()
            }

            this.progressIntervalId = setInterval(() => {
                if (!this.autoMode || !this.slideStartedAt) return

                const elapsedMs = Date.now() - this.slideStartedAt
                const rawPct = (elapsedMs / this.slideDurationMs) * 100
                const nextPct = Math.min(Math.max(rawPct, 0), 100)
                this.activeProgressPct = Number(nextPct.toFixed(2))
                this.publishCarouselRuntime()
            }, this.progressTickMs)
        },

        publishCarouselRuntime(extra = {}) {
            this.$store.dispatch('setDashboardCarouselRuntime', {
                routeActive: true,
                autoMode: this.autoMode,
                slideIndex: this.model,
                slideType: this.activeSlideType,
                activeDeviceId: this.activeDeviceId,
                progressPct: this.autoMode ? this.activeProgressPct : 0,
                slideDurationMs: this.slideDurationMs,
                totalSlides: this.totalSlides,
                isDev: this.isDev,
                ...extra
            })
        },

        clearCarouselRuntime() {
            this.$store.dispatch('setDashboardCarouselRuntime', {
                routeActive: false,
                autoMode: false,
                slideIndex: 0,
                slideType: null,
                activeDeviceId: null,
                progressPct: 0,
                totalSlides: 0
            })
        },

        handleCarouselCommand(commandState) {
            if (!commandState || !commandState.seq) return
            if (commandState.seq <= this.lastHandledCommandSeq) return

            this.lastHandledCommandSeq = commandState.seq
            const command = commandState.command

            if (command === 'go-map') {
                this.goToSlide(0)
                return
            }

            if (command === 'go-comparative') {
                this.goToSlide(this.comparativeSlideIndex)
                return
            }

            if (command === 'prev') {
                this.goToPreviousSlide()
                return
            }

            if (command === 'next') {
                this.goToNextSlide()
                return
            }

            if (command === 'toggle-autoplay') {
                this.toggleAutoMode()
            }
        }
    },

    computed: {
        devices () {
            return this.$store.getters.devices
        },

        project () {
            return this.$store.getters.project
        },

        deviceProject () {
            if (this.devices.length === 0) return []

            const initProject = this.devices[0].projectId
            const selectProject = this.project === 'all' ? initProject : this.project
            const devices = this.devices.filter(e => (
                e.projectId === selectProject &&
                e.deviceId !== EXCLUDED_DASHBOARD_DEVICE_ID
            ))

            return devices.map(e => e.deviceId)
        },

        deviceSlidesOffset() {
            return this.isDev ? 2 : 1
        },

        comparativeSlideIndex() {
            return this.deviceProject.length + this.deviceSlidesOffset
        },

        deviceSlideIndexMap() {
            return this.deviceProject.reduce((acc, deviceId, index) => {
                acc[deviceId] = index + this.deviceSlidesOffset
                return acc
            }, {})
        },

        activeDeviceId() {
            if (this.activeSlideType !== 'device') return null
            const deviceIndex = this.model - this.deviceSlidesOffset
            return this.deviceProject[deviceIndex] || null
        },

        activeSlideType() {
            return this.getSlideType(this.model)
        },

        totalSlides() {
            return this.deviceSlidesOffset + this.deviceProject.length + 1
        },

        dashboardCarouselCommand() {
            return this.$store.getters.dashboardCarouselCommand
        },

        user() {
            return this.$store.getters.user
        },

        isDev() {
            return this.user.roles.some(e => e.name === 'dev')
        }
    },

    watch: {
        project() {
            this.model = 0
        },

        deviceProject(newDevices, oldDevices) {
            if (JSON.stringify(newDevices) !== JSON.stringify(oldDevices)) {
                this.model = 0
            }
        },

        totalSlides(newVal) {
            const lastValidIndex = Math.max(newVal - 1, 0)
            if (this.model > lastValidIndex) {
                this.model = 0
            }
        },

        model() {
            this.$nextTick(() => {
                this.$forceUpdate()
            })

            if (this.autoMode) {
                this.startProgressTracking({ reset: true })
            } else {
                this.activeProgressPct = 0
            }

            this.publishCarouselRuntime()
        },

        autoMode(newVal) {
            if (newVal) {
                this.startProgressTracking({ reset: true })
            } else {
                this.clearProgressTracking()
                this.activeProgressPct = 0
            }

            this.publishCarouselRuntime()
        },

        dashboardCarouselCommand: {
            handler(commandState) {
                this.handleCarouselCommand(commandState)
            }
        }
    },

    components: {
        SimpleChars,
        DataMap,
        DashboardDevicesBar
    },

    created() {
        moment.locale('es-us')
    },

    mounted() {
        this.startProgressTracking({ reset: true })
        this.publishCarouselRuntime()
    },

    beforeDestroy() {
        this.clearProgressTracking()
        this.clearCarouselRuntime()
    }
}
</script>

<style scoped>
.dashboard-wrapper {
    position: relative;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    isolation: isolate;
}

.dashboard-topbar {
    flex: 0 0 auto;
    position: relative;
    z-index: 1;
}

.dashboard-carousel {
    flex: 1 1 auto;
    min-height: 0;
    position: relative;
    overflow: hidden;
}

.dashboard-carousel >>> .v-window,
.dashboard-carousel >>> .v-window__container,
.dashboard-carousel >>> .v-window-item,
.dashboard-carousel >>> .v-carousel__item,
.dashboard-carousel >>> .v-sheet,
.dashboard-carousel >>> .row,
.dashboard-carousel >>> .col {
    min-height: 0;
}

.dashboard-carousel >>> .v-window,
.dashboard-carousel >>> .v-window__container,
.dashboard-carousel >>> .v-window-item,
.dashboard-carousel >>> .v-carousel__item,
.dashboard-carousel >>> .v-sheet {
    height: 100%;
}
</style>
